import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { get, getWithParams } from '../../utils/fetchAPI';
import { FaEdit, FaPlus, FaPills } from 'react-icons/fa';
import PageTitle from "../../components/page-title/pageTitle";
import { AnimatePresence, motion } from "framer-motion";
import Loader from "../../components/loader/loder";
import CustomPagination from "../../components/pagination/CustomPagination";
import { toast } from "react-toastify";
import MedicineForm from "./medicine-form";

const MedicineList = () => {
    const [medicines, setMedicines] = useState([]);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [formOptions, setFormOptions] = useState({ generics: [], types: [], manufacturers: [] });

    // The catalog is large, so only query once the admin pauses typing.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setCurrentPage(1); // Reset to first page on search
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        fetchMedicines();
    }, [currentPage, perPage, search, sourceFilter]);

    useEffect(() => {
        fetchFormOptions();
    }, []);

    const fetchMedicines = async () => {
        setIsLoading(true);
        try {
            const params = {
                per_page: perPage,
                current_page: currentPage,
            };

            if (search) {
                params.search = search;
            }

            if (sourceFilter === 'admin') {
                params.addedByAdmin = true;
            }

            const response = await getWithParams('/medicine/list', params);

            if (response?.status === 200) {
                setMedicines(response.data?.medicines || []);
                setTotalRows(response.data?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching medicines:', error);
            toast.error('Failed to fetch medicines', {
                position: "top-center",
                theme: "light",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Existing generics / dosage forms / manufacturers, offered as dropdown choices in the form.
    const fetchFormOptions = async () => {
        try {
            const response = await get('/medicine/options');

            if (response?.status === 200) {
                setFormOptions({
                    generics: response.data?.generics || [],
                    types: response.data?.types || [],
                    manufacturers: response.data?.manufacturers || [],
                });
            }
        } catch (error) {
            console.error('Error fetching medicine form options:', error);
        }
    };

    const handleEdit = (medicine) => {
        setSelectedMedicine(medicine);
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        setSelectedMedicine(null);
    };

    const handleSaved = (isNew) => {
        fetchFormOptions();

        // A new medicine lands at the top of the first page.
        if (isNew && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }
        fetchMedicines();
    };

    const handleSourceFilterChange = (e) => {
        setSourceFilter(e.target.value);
        setCurrentPage(1); // Reset to first page on filter change
    };

    const columns = [
        {
            name: '#',
            selector: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: '70px',
            center: true,
        },
        {
            name: 'Name',
            selector: row => row?.name,
            wrap: true,
            minWidth: '180px',
        },
        {
            name: 'Generic',
            selector: row => row?.generic || '-',
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Strength',
            selector: row => row?.power || '-',
            wrap: true,
            width: '130px',
        },
        {
            name: 'Dosage Form',
            selector: row => row?.type || '-',
            wrap: true,
            minWidth: '140px',
        },
        {
            name: 'Manufacturer',
            selector: row => row?.manufacturer || '-',
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Added By',
            selector: row => row?.createdBy?.name || '',
            minWidth: '150px',
            cell: (row) => (
                row?.createdBy?.id ? (
                    <span className="px-2 py-1 bg-teal-100 text-teal-800 rounded text-xs">
                        {row?.createdBy?.name || 'Admin'}
                    </span>
                ) : (
                    <span className="text-gray-400">-</span>
                )
            ),
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="flex gap-2">
                    <button
                        className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => handleEdit(row)}
                        title="Edit"
                    >
                        <FaEdit />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 'bold',
            },
        },
        rows: {
            style: {
                fontSize: '0.9rem',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0',
            },
        },
    };

    return (
        <div className="flex justify-center flex-col gap-5 w-full">
            <div className="flex justify-between items-center">
                <PageTitle title="Medicine List" icon={<FaPills />} />
                <button
                    onClick={() => {
                        setSelectedMedicine(null);
                        setShowPopup(true);
                    }}
                    className="flex items-center justify-center gap-2 text-sm px-4 py-2 bg-primary text-white rounded-md hover:bg-teal-700"
                >
                    <FaPlus className="w-4 h-4" />
                    Create New Medicine
                </button>
            </div>

            {/* Search and Filter Section */}
            <div className="bg-white rounded-md p-4 shadow-md">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by name, generic or manufacturer..."
                            className="w-full text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800"
                        />
                    </div>
                    <div className="w-full md:w-48">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Show</label>
                        <select
                            value={sourceFilter}
                            onChange={handleSourceFilterChange}
                            className="w-full text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800"
                        >
                            <option value="">All medicines</option>
                            <option value="admin">Added by admin</option>
                        </select>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center text-gray-500"><Loader /></div>
            ) : (
                <DataTable
                    columns={columns}
                    data={medicines}
                    customStyles={customStyles}
                    highlightOnHover
                    striped
                    noDataComponent="No medicines found"
                />
            )}

            <CustomPagination
                currentPage={currentPage}
                totalRows={totalRows}
                perPage={perPage}
                setCurrentPage={setCurrentPage}
                setPerPage={setPerPage}
            />

            <AnimatePresence>
                {showPopup && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="bg-white p-5 rounded-md shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                        >
                            <MedicineForm
                                medicine={selectedMedicine}
                                genericOptions={formOptions.generics}
                                typeOptions={formOptions.types}
                                manufacturerOptions={formOptions.manufacturers}
                                onSuccess={handleSaved}
                                onClose={handleClosePopup}
                            />
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MedicineList;
